import React, { useLayoutEffect, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { termsConfig as defaultTermsConfig } from '../config/termsConfig';
import { loadTermsConfigForLocale } from '../config/configRegistry';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { SITE_ORIGIN } from '../config/site';
import SiteHeader from './SiteHeader';
import Footer from './Footer';
import { setPrerenderReady } from '../utils/prerenderUtils';

const Terms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, ready } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [termsConfig, setTermsConfig] = useState(defaultTermsConfig);

  // Load localized terms config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await loadTermsConfigForLocale(currentLanguage);
        setTermsConfig(config);
      } catch (error) {
        console.error('Failed to load terms config:', error);
        setTermsConfig(defaultTermsConfig);
      }
    };
    loadConfig();
  }, [currentLanguage]);

  const handleBack = (e) => {
    e.preventDefault();
    navigate(-1); // This will go back one step in the history
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const renderSection = (section) => {
    return (
      <div className="prose max-w-none">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {section.title}
        </h2>
        {section.subsections ? (
          <div className="space-y-6 pl-4">
            {section.subsections.map((subsection, subIndex) => (
              <div key={subIndex} className="border-l-2 border-gray-100 pl-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {subsection.title}
                </h3>
                <p className="text-gray-600">
                  {subsection.content}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">
            {section.content}
          </p>
        )}
      </div>
    );
  };

  // Set prerender ready when translations are loaded
  useEffect(() => {
    if (ready) {
      setPrerenderReady();
    }
  }, [ready]);

  // Show loading state until translations are ready
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <html lang={currentLanguage} />
        <title>{t('legal.terms', 'Terms of Service')}</title>
        <meta name="description" content={t('legal.termsDescription', "LiveLoveSpain's Terms of Service - Understanding your rights and responsibilities when using our platform.")} />
        <meta property="og:title" content={`${t('legal.terms', 'Terms of Service')} - LiveLoveSpain`} />
        <meta property="og:description" content={t('legal.termsDescription', "LiveLoveSpain's Terms of Service - Understanding your rights and responsibilities when using our platform.")} />
        <meta property="og:url" content={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/terms`} />
        <meta name="twitter:title" content={`${t('legal.terms', 'Terms of Service')} - LiveLoveSpain`} />
        <meta name="twitter:description" content={t('legal.termsDescription', "LiveLoveSpain's Terms of Service - Understanding your rights and responsibilities when using our platform.")} />
        <meta name="twitter:url" content={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/terms`} />
        <link rel="canonical" href={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/terms`} />
        
        {/* Breadcrumb Schema */}
        <script key="ld:page" type="application/ld+json" data-page={`${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/terms`}>
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [{
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": t('common.home', 'Home'),
                  "item": `${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}`
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": t('legal.terms', 'Terms of Service'),
                  "item": `${SITE_ORIGIN}${currentLanguage === 'en' ? '' : `/${currentLanguage}`}/terms`
                }
              ]
            }]}
          )}
        </script>
      </Helmet>

      {/* Header */}
      <SiteHeader />

      {/* Visual breadcrumb navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-blue-600 transition-colors">
              {t('common.home')}
            </Link>
          </li>
          <li>
            <span className="mx-2">/</span>
          </li>
          <li className="text-gray-900 font-medium">
            {t('legal.terms', 'Terms of Service')}
          </li>
        </ol>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('legal.cookiesPage.back')}
          </button>

          {/* Title and Last Updated */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {termsConfig.title}
            </h1>
            <p className="text-sm text-gray-500">
              {t('legal.cookiesPage.lastUpdated')} {termsConfig.lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <p className="text-gray-600 mb-8">
            {termsConfig.intro}
          </p>

          {/* Table of Contents */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Table of Contents</h2>
            <nav>
              <ul className="space-y-2">
                {termsConfig.sections.map((section, index) => (
                  <li key={index}>
                    <a href={`#section-${index}`} className="text-blue-600 hover:text-blue-700">
                      {section.title}
                    </a>
                    {section.subsections && (
                      <ul className="pl-4 mt-2 space-y-1">
                        {section.subsections.map((subsection, subIndex) => (
                          <li key={subIndex}>
                            <a href={`#section-${index}-${subIndex}`} className="text-sm text-gray-600 hover:text-blue-600">
                              {subsection.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {termsConfig.sections.map((section, index) => (
              <div key={index} id={`section-${index}`}>
                {renderSection(section)}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Terms; 